// Pure helpers for the talent platform's debug-note capture — the talent-side
// twin of the buyer app's debug-note plumbing. Kept free of React/DOM so they
// unit-test as plain functions (the seller test env is jsdom, but these don't
// need it).

// The talent debug note posts to the SAME buyer-scoped route the client SPA uses
// (`POST /api/debug-notes`, guarded by `require_buyer`). The talent SPA has no
// buyer identity (only the `X-Seller-Id` stub), so it presents a self-describing
// guest buyer id: `require_buyer` accepts ANY `guest_*` X-Buyer-Id in fiverr
// (cloud) mode and trusts the header in stub mode, so a buyer-less talent still
// authenticates. The simulated talent id rides along so the admin "Debug Notes"
// row shows which talent logged it.
const DEBUG_NOTE_BUYER_PREFIX = "guest_talent_";

/** The stub buyer id a talent debug note rides on (see the note above). Falls
 * back to `directory` when no talent is being simulated (the picker view). */
export function talentDebugBuyerId(sellerId: string): string {
  return DEBUG_NOTE_BUYER_PREFIX + (sellerId.trim() || "directory");
}

// Remembers the tester's name across notes (and reloads) so they only type it
// once — same `recruiter:*` key convention as the rest of the app's local state,
// and the SAME key the buyer app uses so a tester on both surfaces types it once.
export const TESTER_NAME_KEY = "recruiter:debug-tester-name";

// Fold the tester's name into the note text as a two-line string, so the whole
// thing lands in the single `note_text` column (no schema/wire change) and reads
// `name: <who>` / `note: <what>` in the admin Note cell. Pure so it's unit-tested.
// Byte-identical to the buyer app's `composeDebugNote` so both surfaces render
// the same way in the admin tab.
export function composeDebugNote(name: string, note: string): string {
  return `name: ${name.trim()}\nnote: ${note.trim()}`;
}

// Read/write the remembered tester name against a Storage — the app passes
// `window.localStorage`; tests pass a fake. Best-effort: storage may be
// unavailable (private mode), in which case we just don't remember.
export function readTesterName(storage: Storage | undefined): string {
  try {
    return storage?.getItem(TESTER_NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function writeTesterName(storage: Storage | undefined, value: string): void {
  try {
    storage?.setItem(TESTER_NAME_KEY, value);
  } catch {
    /* best-effort — remembering the name is a convenience, not load-bearing. */
  }
}
